import { useState, useCallback } from 'react'
import './ExportTab.css'
import { API_BASE as API_BASE_CONFIG } from '../../../config'

// Every /api/export route requires a bearer token. JSON POSTs must send it as a
// header; file downloads must be fetched as a blob (a plain <a>/window.open
// navigation can't attach the Authorization header, so it 401s as well).
const _xlr8AuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('xlr8_token') || ''}`,
})

const _downloadAuthedFile = async (url, filename) => {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${localStorage.getItem('xlr8_token') || ''}` },
  })
  if (!res.ok) throw new Error(`Download failed (${res.status})`)
  const blob = await res.blob()
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objUrl)
}

/**
 * ExportTab — Consulting-grade export experience.
 * Handles three modes:
 *   1. Ad-hoc query result (lastDataMsg) — CSV download + Excel
 *   2. Full investigation complete — multi-sheet report
 *   3. Export generated — download ready
 */
export default function ExportTab({
  showReady = false,
  exportResult,
  customerId,
  customer,
  findings = [],
  actions = [],
  planTitle = '',
  workState = 'empty',
  onGenerate,
  lastDataMsg = null,
}) {
  const [generating, setGenerating] = useState(false)
  const [toggles, setToggles] = useState({
    provenance: true,
    sql: false,
    samples: true,
    workplan: true,
  })

  const criticalCount = findings.filter(f => f.type === 'critical').length
  const warningCount  = findings.filter(f => f.type === 'warning').length
  const compatibleCount = findings.filter(f => f.type === 'compatible').length
  const hasResults = findings.length > 0 || actions.length > 0
  const hasQueryResult = lastDataMsg && lastDataMsg.data && lastDataMsg.data.length > 0

  const handleGenerate = async () => {
    if (!onGenerate) return
    setGenerating(true)
    try {
      await onGenerate('work_effort', toggles)
    } finally {
      setGenerating(false)
    }
  }

  // ── CSV download — fetches detail records from backend, falls back to summary ──
  const [csvGenerating, setCsvGenerating] = useState(false)

  const _downloadCsv = (data, cols, filename) => {
    const header = cols.join(',')
    const rows = data.map(row =>
      cols.map(col => {
        const val = row[col] ?? ''
        const str = String(val)
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"` : str
      }).join(',')
    )
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename.replace(/\s+/g, '_')
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleCsvDownload = useCallback(async () => {
    if (!hasQueryResult) return
    const filename = `${customer?.company_name || customerId}_export.csv`
    const summaryData = lastDataMsg.data
    const summaryCols = lastDataMsg.columns || Object.keys(summaryData[0] || {})

    // If aggregate result (small row count, count-like columns) — fetch detail from backend
    const aggKeywords = ['count', 'total', 'sum', 'employee_count', 'headcount', 'num', 'number']
    const isAggregate = summaryCols.some(c => aggKeywords.some(k => c.toLowerCase().includes(k))) && summaryData.length <= 10

    if (isAggregate && lastDataMsg.sql) {
      setCsvGenerating(true)
      try {
        const API_BASE = API_BASE_CONFIG
        const response = await fetch(`${API_BASE}/api/export/query-result`, {
          method: 'POST',
          headers: _xlr8AuthHeaders(),
          body: JSON.stringify({
            customer_id: customerId,
            data: summaryData,
            columns: summaryCols,
            sql: lastDataMsg.sql,
            row_count: lastDataMsg.row_count || summaryData.length,
            title: 'Query Result',
            format: 'csv_detail',
          }),
        })
        const result = await response.json()
        if (result.success && result.detail_data && result.detail_data.length > 0) {
          _downloadCsv(result.detail_data, result.detail_columns, filename)
          return
        }
      } catch (err) {
        console.warn('Detail fetch failed, falling back to summary CSV:', err)
      } finally {
        setCsvGenerating(false)
      }
    }

    // Fallback: download whatever data we have
    _downloadCsv(summaryData, summaryCols, filename)
  }, [lastDataMsg, customer, customerId, hasQueryResult])

  // ── Excel export via backend ──
  const [excelGenerating, setExcelGenerating] = useState(false)
  const [excelResult, setExcelResult] = useState(null)

  const handleExcelDownload = useCallback(async () => {
    if (!hasQueryResult) return
    setExcelGenerating(true)
    try {
      const API_BASE = API_BASE_CONFIG
      const response = await fetch(`${API_BASE}/api/export/query-result`, {
        method: 'POST',
        headers: _xlr8AuthHeaders(),
        body: JSON.stringify({
          customer_id: customerId,
          data: lastDataMsg.data,
          columns: lastDataMsg.columns || Object.keys(lastDataMsg.data[0] || {}),
          sql: lastDataMsg.sql || null,
          row_count: lastDataMsg.row_count || lastDataMsg.data.length,
          title: planTitle || 'Query Result',
        }),
      })
      const result = await response.json()
      if (result.success && result.download_url) {
        const downloadUrl = result.download_url.startsWith('http')
          ? result.download_url : `${API_BASE}${result.download_url}`
        setExcelResult({ url: downloadUrl, filename: result.filename || 'query_result.xlsx' })
        await _downloadAuthedFile(downloadUrl, result.filename || 'query_result.xlsx')
      } else {
        setExcelResult({ error: result.error || result.detail || 'Export failed' })
      }
    } catch (err) {
      setExcelResult({ error: err.message })
    } finally {
      setExcelGenerating(false)
    }
  }, [hasQueryResult, lastDataMsg, customerId, planTitle])

  // ── EXPORT COMPLETE — download ready ──
  if (showReady && exportResult && !exportResult.error) {
    const API_BASE = API_BASE_CONFIG
    const downloadUrl = exportResult.downloadUrl?.startsWith('http')
      ? exportResult.downloadUrl
      : `${API_BASE}${exportResult.downloadUrl}`

    return (
      <div className="ext">
        <div className="ext-status-bar ext-status-ready">
          <span className="ext-status-dot ext-dot-green" />
          Report generated successfully
        </div>

        <div className="ext-section">
          <div className="ext-label">CLIENT</div>
          <div className="ext-value-lg">{customer?.company_name || customerId}</div>
          <div className="ext-value-sm">{planTitle || 'Work Effort Analysis'}</div>
        </div>

        <div className="ext-divider" />

        <div className="ext-kpi-row">
          <div className="ext-kpi">
            <div className="ext-kpi-val">{exportResult.sheetCount || '—'}</div>
            <div className="ext-kpi-label">Sheets</div>
          </div>
          <div className="ext-kpi">
            <div className="ext-kpi-val">{exportResult.totalRows?.toLocaleString() || '—'}</div>
            <div className="ext-kpi-label">Rows</div>
          </div>
          <div className="ext-kpi">
            <div className="ext-kpi-val">{findings.length}</div>
            <div className="ext-kpi-label">Findings</div>
          </div>
          <div className="ext-kpi">
            <div className="ext-kpi-val">{actions.length}</div>
            <div className="ext-kpi-label">Actions</div>
          </div>
        </div>

        <div className="ext-divider" />

        {exportResult.sheetDetails && exportResult.sheetDetails.length > 0 && (
          <div className="ext-section">
            <div className="ext-label">CONTENTS</div>
            {exportResult.sheetDetails.map((sheet, i) => (
              <div key={i} className="ext-detail-row">
                <span className="ext-detail-name">{sheet.name}</span>
                <span className="ext-detail-val">{sheet.rows?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}

        <button
          className="ext-btn-primary"
          onClick={() => _downloadAuthedFile(downloadUrl, exportResult.filename || 'report.xlsx')}
        >
          Download Report
        </button>
        <div className="ext-filename">{exportResult.filename || 'report.xlsx'}</div>
      </div>
    )
  }

  // ── EXPORT ERROR ──
  if (showReady && exportResult?.error) {
    return (
      <div className="ext">
        <div className="ext-status-bar ext-status-error">
          <span className="ext-status-dot ext-dot-red" />
          Export failed
        </div>
        <div className="ext-section">
          <div className="ext-error-text">{exportResult.error}</div>
        </div>
        <button className="ext-btn-secondary" onClick={handleGenerate}>Retry</button>
      </div>
    )
  }

  // ── INVESTIGATION COMPLETE — multi-sheet report ──
  if (hasResults && (workState === 'complete' || workState === 'export')) {
    return (
      <div className="ext">
        <div className="ext-section">
          <div className="ext-label">CLIENT</div>
          <div className="ext-value-lg">{customer?.company_name || customerId}</div>
          <div className="ext-value-sm">{planTitle || 'Work Effort Analysis'}</div>
        </div>

        <div className="ext-divider" />

        <div className="ext-section">
          <div className="ext-label">FINDINGS SUMMARY</div>
          <div className="ext-kpi-row">
            {criticalCount > 0 && (
              <div className="ext-kpi">
                <div className="ext-kpi-val ext-val-red">{criticalCount}</div>
                <div className="ext-kpi-label">Critical</div>
              </div>
            )}
            {warningCount > 0 && (
              <div className="ext-kpi">
                <div className="ext-kpi-val ext-val-amber">{warningCount}</div>
                <div className="ext-kpi-label">Warning</div>
              </div>
            )}
            {compatibleCount > 0 && (
              <div className="ext-kpi">
                <div className="ext-kpi-val ext-val-green">{compatibleCount}</div>
                <div className="ext-kpi-label">Compatible</div>
              </div>
            )}
            <div className="ext-kpi">
              <div className="ext-kpi-val">{actions.length}</div>
              <div className="ext-kpi-label">Actions</div>
            </div>
          </div>
        </div>

        <div className="ext-divider" />

        <div className="ext-section">
          <div className="ext-label">REPORT CONTENTS</div>
          <div className="ext-checklist">
            <div className="ext-check-item"><span className="ext-check" />Executive summary with KPIs</div>
            <div className="ext-check-item"><span className="ext-check" />{findings.length} findings with severity ratings</div>
            <div className="ext-check-item"><span className="ext-check" />{actions.length} remediation actions with estimates</div>
            <div className="ext-check-item"><span className="ext-check" />Employee-level detail and outlier flags</div>
            <div className="ext-check-item"><span className="ext-check" />Client branding, confidential footer</div>
          </div>
        </div>

        <div className="ext-divider" />

        <div className="ext-section">
          <div className="ext-label">OPTIONS</div>
          {[
            { key: 'provenance', label: 'Source citations' },
            { key: 'samples',    label: 'Raw data samples' },
            { key: 'workplan',   label: 'Remediation workplan' },
            { key: 'sql',        label: 'SQL queries' },
          ].map(opt => (
            <div key={opt.key} className="ext-toggle-row">
              <span className="ext-toggle-label">{opt.label}</span>
              <div
                className={`ext-toggle ${toggles[opt.key] ? 'on' : ''}`}
                onClick={() => setToggles(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))}
              />
            </div>
          ))}
        </div>

        <button
          className="ext-btn-primary"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? 'Generating…' : 'Generate Report'}
        </button>
        <div className="ext-format-note">Excel (.xlsx) · Multi-sheet workbook</div>
      </div>
    )
  }

  // ── AD-HOC QUERY RESULT — single query, no investigation ──
  if (hasQueryResult) {
    const rowCount = lastDataMsg.row_count || lastDataMsg.data.length
    const cols = lastDataMsg.columns || Object.keys(lastDataMsg.data[0] || {})
    return (
      <div className="ext">
        <div className="ext-status-bar ext-status-ready">
          <span className="ext-status-dot ext-dot-green" />
          Query result ready to export
        </div>

        <div className="ext-section">
          <div className="ext-label">RESULT</div>
          <div className="ext-value-lg">{rowCount.toLocaleString()} rows</div>
          <div className="ext-value-sm">{cols.length} columns · {customer?.company_name || customerId}</div>
        </div>

        {lastDataMsg.sql && (
          <>
            <div className="ext-divider" />
            <div className="ext-section">
              <div className="ext-label">SQL</div>
              <pre className="ext-sql-preview">{lastDataMsg.sql.length > 200
                ? lastDataMsg.sql.slice(0, 200) + '…'
                : lastDataMsg.sql}
              </pre>
            </div>
          </>
        )}

        <div className="ext-divider" />

        <button className="ext-btn-primary" onClick={handleCsvDownload} disabled={csvGenerating}>
          {csvGenerating ? 'Fetching detail…' : '↓ Download CSV'}
        </button>

        <button
          className="ext-btn-secondary"
          onClick={handleExcelDownload}
          disabled={excelGenerating}
          style={{ marginTop: '8px' }}
        >
          {excelGenerating ? 'Generating…' : '↓ Download Excel (.xlsx)'}
        </button>

        {excelResult?.error && (
          <div className="ext-error-text" style={{ marginTop: '8px' }}>{excelResult.error}</div>
        )}

        <div className="ext-format-note">CSV downloads instantly · Excel requires a moment</div>
      </div>
    )
  }

  // ── EMPTY — nothing yet ──
  return (
    <div className="ext">
      <div className="ext-empty">
        <div className="ext-empty-title">No results to export</div>
        <div className="ext-empty-text">
          Ask a question to get query results, or complete an investigation to generate a full report.
        </div>
        {findings.length > 0 && (
          <div className="ext-empty-partial">{findings.length} findings collected — finish investigation to export.</div>
        )}
      </div>
    </div>
  )
}
